import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { completionSummaryText, getFamilyQuantity, getMyAssignedQuantity, isFamilyChecklistKind, isMineChecked, isMineItem, } from "@/lib/planAssign";
import { SearchInput } from "@/components/plan/SearchInput";
import { ItemDetailModal } from "./ItemDetailModal";
const STATUS_OPTIONS = [
    { value: "open", label: "לא נארזו" },
    { value: "all", label: "הכל" },
    { value: "done", label: "נארזו" },
];
const EMPTY_BY_STATUS = {
    open: "אין פריטים פתוחים",
    all: "אין פריטים שהוקצו לך עדיין",
    done: "אין פריטים שבוצעו",
};
function chipClass(active) {
    return `rounded-full border px-3 py-1 text-xs transition-colors ${active
        ? "border-indigo-400 bg-indigo-50 text-indigo-700 font-medium"
        : "border-slate-200 text-slate-600 hover:bg-slate-50"}`;
}
/** Dedicated "שלי" page: every item assigned to me, grouped by category. */
export function MyItemsTab({ content, myDisplayName, myParticipantId, participants, onToggleMyItem, onUpdateItem, }) {
    const [status, setStatus] = useState("open");
    const [searchQuery, setSearchQuery] = useState("");
    const [detailId, setDetailId] = useState(null);
    const normalizedSearch = searchQuery.trim().toLowerCase();
    // Collect my items grouped by section → subsection, deduped by item id so an
    // item never shows twice even if data integrity slips.
    const seen = new Set();
    const groups = [];
    for (const section of content.sections) {
        const isFamily = isFamilyChecklistKind(section.sectionKind);
        const subs = [];
        for (const sub of section.subsections) {
            const items = [];
            for (const item of sub.items) {
                // Family-checklist items belong to the current family without any
                // assignment; everything else must be assigned to me. Shared helpers keep
                // this in lockstep with the "אצלנו" overview metric.
                if (!isMineItem(item, section.sectionKind, myParticipantId, myDisplayName))
                    continue;
                if (seen.has(item.id))
                    continue;
                const myChecked = isMineChecked(item, section.sectionKind, myParticipantId, myDisplayName);
                if (status === "open" && myChecked)
                    continue;
                if (status === "done" && !myChecked)
                    continue;
                if (normalizedSearch && !item.text.toLowerCase().includes(normalizedSearch))
                    continue;
                seen.add(item.id);
                items.push({
                    item,
                    sectionId: section.id,
                    subId: sub.id,
                    sectionTitle: section.title,
                    subTitle: sub.title,
                    sectionKind: isFamily ? "family_checklist" : section.sectionKind ?? "normal",
                });
            }
            if (items.length > 0)
                subs.push({ subId: sub.id, subTitle: sub.title, items });
        }
        if (subs.length > 0) {
            groups.push({
                sectionId: section.id,
                sectionTitle: section.title,
                sectionIcon: section.icon,
                sectionKind: isFamily ? "family_checklist" : section.sectionKind ?? "normal",
                subs,
            });
        }
    }
    const emptyMessage = normalizedSearch
        ? "לא נמצאו פריטים מתאימים"
        : EMPTY_BY_STATUS[status];
    // Re-look-up the open item from live content so edits/deletions reflect at
    // once and the modal always edits the original (same pattern as CollectionView).
    let liveDetail = null;
    if (detailId) {
        for (const section of content.sections) {
            for (const sub of section.subsections) {
                const found = sub.items.find((i) => i.id === detailId);
                if (found) {
                    liveDetail = {
                        item: found,
                        sectionId: section.id,
                        subId: sub.id,
                        sectionTitle: section.title,
                        subTitle: sub.title,
                        sectionKind: section.sectionKind ?? "normal",
                    };
                }
            }
        }
    }
    return (_jsxs("div", { className: "space-y-4", dir: "rtl", children: [_jsx(SearchInput, { value: searchQuery, onChange: setSearchQuery, placeholder: "\u05D7\u05D9\u05E4\u05D5\u05E9 \u05D1\u05E4\u05E8\u05D9\u05D8\u05D9\u05DD \u05E9\u05DC\u05D9\u2026" }), _jsx("div", { className: "flex items-center gap-1.5", children: STATUS_OPTIONS.map((opt) => (_jsx("button", { onClick: () => setStatus(opt.value), "aria-pressed": status === opt.value, className: chipClass(status === opt.value), children: opt.label }, opt.value))) }), groups.length === 0 ? (_jsx("div", { className: "rounded-2xl bg-white border border-slate-100 shadow-sm px-6 py-8 text-center text-sm text-slate-400", children: emptyMessage })) : (groups.map((group) => (_jsxs("div", { className: "rounded-2xl bg-white shadow-sm border border-slate-100 overflow-hidden", children: [_jsx("div", { className: "border-b border-slate-100 px-4 py-3", children: _jsxs("h2", { className: "flex items-center gap-2 text-sm font-bold text-slate-800", children: [group.sectionIcon && _jsx("span", { "aria-hidden": true, children: group.sectionIcon }), group.sectionTitle || "(ללא שם)", group.sectionKind === "family_checklist" && (_jsx("span", { className: "rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-600", children: "\uD83D\uDC6A \u05DC\u05DB\u05DC \u05DE\u05E9\u05E4\u05D7\u05D4" }))] }) }), _jsx("div", { className: "divide-y divide-slate-50", children: group.subs.map((sub) => (_jsxs("div", { className: "px-4 py-2", children: [sub.subTitle && (_jsx("h3", { className: "mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400", children: sub.subTitle })), _jsx("ul", { className: "space-y-1", children: sub.items.map(({ item, sectionId, subId, sectionKind }) => {
                                        const isFamily = isFamilyChecklistKind(sectionKind);
                                        const myChecked = isMineChecked(item, sectionKind, myParticipantId, myDisplayName);
                                        const myQty = isFamily
                                            ? getFamilyQuantity(item)
                                            : getMyAssignedQuantity(item, myParticipantId, myDisplayName);
                                        // "החלק שלי" = my personal part; the aggregate "בוצע X/Y"
                                        // can still be partial globally even after I pack my part.
                                        // Family-checklist rows are per-family packing, never a split.
                                        const mineLabel = isFamily
                                            ? myChecked
                                                ? "נארז אצלנו"
                                                : myQty > 1
                                                    ? `כמות למשפחה: ${myQty}`
                                                    : "אצל המשפחה שלנו"
                                            : myChecked
                                                ? "החלק שלי נארז"
                                                : myQty > 0
                                                    ? `החלק שלי: ${myQty}`
                                                    : "החלק שלי";
                                        const aggregate = isFamily ? "" : completionSummaryText(item);
                                        return (_jsx("li", { children: _jsxs("div", { role: "button", tabIndex: 0, onClick: () => setDetailId(item.id), onKeyDown: (e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        setDetailId(item.id);
                                                    }
                                                }, className: "flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1.5 text-sm hover:bg-slate-50", children: [_jsx("button", { onClick: (e) => {
                                                            e.stopPropagation();
                                                            onToggleMyItem(item.id, sectionId, subId, myParticipantId, myDisplayName);
                                                        }, "aria-label": myChecked ? "בטל סימון אריזה" : "סמן שנארז", className: `flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all ${myChecked
                                                            ? "border-emerald-500 bg-emerald-500 text-white"
                                                            : "border-slate-300 hover:border-emerald-400"}`, children: myChecked && (_jsx("svg", { width: "10", height: "10", viewBox: "0 0 12 12", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M2 6l3 3 5-5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) })) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("span", { className: `block truncate ${myChecked ? "text-slate-400 line-through" : "text-slate-700"}`, children: item.text }), _jsxs("span", { className: "block truncate text-xs text-slate-400", children: [mineLabel, aggregate ? ` · ${aggregate}` : ""] })] })] }) }, item.id));
                                    }) })] }, sub.subId))) })] }, group.sectionId)))), liveDetail && (_jsx(ItemDetailModal, { item: liveDetail.item, sectionTitle: liveDetail.sectionTitle, subsectionTitle: liveDetail.subTitle, sectionKind: liveDetail.sectionKind, participants: participants, myDisplayName: myDisplayName, myParticipantId: myParticipantId, onUpdate: (patch) => onUpdateItem(liveDetail.sectionId, liveDetail.subId, liveDetail.item.id, patch), onClose: () => setDetailId(null) }))] }));
}
